import { Component, forwardRef, OnInit, OnDestroy, HostListener, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface PeriodRange {
  startDate: Date;
  endDate: Date;
}

export type PeriodType = 'ytd' | 'monthly' | 'quarterly' | 'yearly';

interface PeriodSelection {
  year: number;
  period: number; // month (1-12), quarter (1-4), or 0 for yearly
}

export interface PeriodRangeValue {
  periodType: PeriodType;
  rangeStart: PeriodSelection | null;
  rangeEnd: PeriodSelection | null;
  dateRange: PeriodRange | null;
}

@Component({
  selector: 'app-period-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeriodRangePickerComponent),
      multi: true
    }
  ],
  templateUrl: './period-range-picker.component.html',
  styleUrls: ['./period-range-picker.component.scss']
})
export class PeriodRangePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  // Period type
  periodType: PeriodType = 'ytd';
  
  // Picker state
  pickerOpen = false;
  currentViewYear: number = new Date().getFullYear();
  dropdownPosition: 'below' | 'above' = 'below';
  
  // Range selection
  rangeStart: PeriodSelection | null = null;
  rangeEnd: PeriodSelection | null = null;
  
  // Month data
  months = [
    { value: 1, label: 'January', short: 'Jan' },
    { value: 2, label: 'February', short: 'Feb' },
    { value: 3, label: 'March', short: 'Mar' },
    { value: 4, label: 'April', short: 'Apr' },
    { value: 5, label: 'May', short: 'May' },
    { value: 6, label: 'June', short: 'Jun' },
    { value: 7, label: 'July', short: 'Jul' },
    { value: 8, label: 'August', short: 'Aug' },
    { value: 9, label: 'September', short: 'Sep' },
    { value: 10, label: 'October', short: 'Oct' },
    { value: 11, label: 'November', short: 'Nov' },
    { value: 12, label: 'December', short: 'Dec' }
  ];
  
  // Quarter data
  quarters = [
    { value: 1, label: 'Q1', months: 'Jan - Mar' },
    { value: 2, label: 'Q2', months: 'Apr - Jun' },
    { value: 3, label: 'Q3', months: 'Jul - Sep' },
    { value: 4, label: 'Q4', months: 'Oct - Dec' }
  ];

  // Min/Max dates (configurable via inputs)
  @Input() minDate: Date = new Date('2023-12-06');
  @Input() maxDate: Date = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1); // Yesterday
    return d;
  })();

  // ControlValueAccessor properties
  private onChange: (value: PeriodRangeValue | null) => void = () => {};
  private onTouched: () => void = () => {};
  disabled = false;

  ngOnInit(): void {
    this.currentViewYear = new Date().getFullYear();
    this.initializeDefaultRange();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Listen for Escape key to close picker
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.pickerOpen) {
      this.closePicker();
      event.preventDefault();
    }
  }

  /**
   * Initialize default YTD range
   */
  initializeDefaultRange(): void {
    if (this.periodType === 'ytd') {
      this.calculateAndEmitRange();
    }
  }

  /**
   * Get year range for yearly view (show 12 years at a time)
   */
  get yearRange(): number[] {
    const startYear = Math.floor(this.currentViewYear / 12) * 12;
    const years: number[] = [];
    for (let i = 0; i < 12; i++) {
      years.push(startYear + i);
    }
    return years;
  }

  /**
   * Get display string for selected range
   */
  get rangeDisplay(): string {
    if (this.periodType === 'ytd') {
      return this.ytdRangeDisplay;
    }
    
    if (!this.rangeStart || !this.rangeEnd) {
      return '';
    }

    const startStr = this.formatPeriodSelection(this.rangeStart);
    const endStr = this.formatPeriodSelection(this.rangeEnd);
    return `${startStr} - ${endStr}`;
  }

  /**
   * Format period selection for display
   */
  private formatPeriodSelection(selection: PeriodSelection): string {
    switch (this.periodType) {
      case 'monthly':
        const month = this.months.find(m => m.value === selection.period);
        return `${month?.short} ${selection.year}`;
      case 'quarterly':
        return `Q${selection.period} ${selection.year}`;
      case 'yearly':
        return `${selection.year}`;
      default:
        return '';
    }
  }

  /**
   * Get YTD range display string
   */
  get ytdRangeDisplay(): string {
    const lastYear = new Date().getFullYear() - 1;
    const today = new Date();
    const startDate = new Date(lastYear, today.getMonth(), today.getDate());
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    return `${startDate.toLocaleDateString('en-GB')} - ${endDate.toLocaleDateString('en-GB')}`;
  }

  // ===== Picker Actions =====
  
  openPicker(): void {
    if (!this.disabled) {
      this.pickerOpen = true;
      // Calculate position after view updates
      setTimeout(() => {
        this.calculateDropdownPosition();
      }, 0);
    }
  }

  closePicker(): void {
    this.pickerOpen = false;
    this.onTouched();
  }

  /**
   * Calculate whether to open dropdown above or below based on available space
   */
  private calculateDropdownPosition(): void {
    const inputElement = document.querySelector('.period-range-field');
    if (!inputElement) return;

    const rect = inputElement.getBoundingClientRect();
    const dropdownHeight = 450; // Approximate dropdown height including header, content, and actions
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const margin = 4; // Gap between input and dropdown

    // If not enough space below and more space above, open upward
    if (spaceBelow < dropdownHeight + margin && spaceAbove > spaceBelow) {
      this.dropdownPosition = 'above';
    } else {
      this.dropdownPosition = 'below';
    }
  }

  previousYear(): void {
    if (this.periodType === 'yearly') {
      this.currentViewYear -= 12;
    } else {
      this.currentViewYear--;
    }
  }

  nextYear(): void {
    if (this.periodType === 'yearly') {
      this.currentViewYear += 12;
    } else {
      this.currentViewYear++;
    }
  }

  // ===== Period Type Change =====
  
  onPeriodTypeChange(): void {
    this.rangeStart = null;
    this.rangeEnd = null;
    this.calculateAndEmitRange();
  }

  // ===== Selection Methods =====
  
  selectMonth(year: number, month: number): void {
    this.selectPeriod({ year, period: month });
  }

  selectQuarter(year: number, quarter: number): void {
    this.selectPeriod({ year, period: quarter });
  }

  selectYear(year: number): void {
    this.selectPeriod({ year, period: 0 });
  }

  private selectPeriod(selection: PeriodSelection): void {
    if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
      // Start new range
      this.rangeStart = selection;
      this.rangeEnd = null;
    } else {
      // Complete the range
      if (this.comparePeriods(selection, this.rangeStart) < 0) {
        // Selected period is before start, swap them
        this.rangeEnd = this.rangeStart;
        this.rangeStart = selection;
      } else {
        this.rangeEnd = selection;
      }
      this.calculateAndEmitRange();
    }
  }

  clearSelection(): void {
    this.rangeStart = null;
    this.rangeEnd = null;
    this.calculateAndEmitRange();
  }

  // ===== Helper Methods for Styling =====
  
  isMonthSelected(year: number, month: number): boolean {
    return (this.rangeStart?.year === year && this.rangeStart?.period === month) ||
           (this.rangeEnd?.year === year && this.rangeEnd?.period === month);
  }

  isMonthInRange(year: number, month: number): boolean {
    if (!this.rangeStart || !this.rangeEnd) return false;
    const current = { year, period: month };
    return this.comparePeriods(current, this.rangeStart) >= 0 &&
           this.comparePeriods(current, this.rangeEnd) <= 0;
  }

  isQuarterSelected(year: number, quarter: number): boolean {
    return (this.rangeStart?.year === year && this.rangeStart?.period === quarter) ||
           (this.rangeEnd?.year === year && this.rangeEnd?.period === quarter);
  }

  isQuarterInRange(year: number, quarter: number): boolean {
    if (!this.rangeStart || !this.rangeEnd) return false;
    const current = { year, period: quarter };
    return this.comparePeriods(current, this.rangeStart) >= 0 &&
           this.comparePeriods(current, this.rangeEnd) <= 0;
  }

  isYearSelected(year: number): boolean {
    return (this.rangeStart?.year === year) || (this.rangeEnd?.year === year);
  }

  isYearInRange(year: number): boolean {
    if (!this.rangeStart || !this.rangeEnd) return false;
    return year >= this.rangeStart.year && year <= this.rangeEnd.year;
  }

  isRangeStart(year: number, period: number, type: PeriodType): boolean {
    return this.rangeStart?.year === year && (type === 'yearly' || this.rangeStart?.period === period);
  }

  isRangeEnd(year: number, period: number, type: PeriodType): boolean {
    return this.rangeEnd?.year === year && (type === 'yearly' || this.rangeEnd?.period === period);
  }

  // ===== Disabled State Checks =====

  isMonthDisabled(year: number, month: number): boolean {
    // Get first day of the month
    const monthStart = new Date(year, month - 1, 1);
    // Get last day of the month
    const monthEnd = new Date(year, month, 0);
    
    // Month is disabled if it's entirely outside the min/max range
    return monthEnd < this.minDate || monthStart > this.maxDate;
  }

  isQuarterDisabled(year: number, quarter: number): boolean {
    // Get first day of the quarter
    const quarterStartMonth = (quarter - 1) * 3;
    const quarterStart = new Date(year, quarterStartMonth, 1);
    // Get last day of the quarter
    const quarterEnd = new Date(year, quarterStartMonth + 3, 0);
    
    // Quarter is disabled if it's entirely outside the min/max range
    return quarterEnd < this.minDate || quarterStart > this.maxDate;
  }

  isYearDisabled(year: number): boolean {
    // Get first and last day of the year
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    
    // Year is disabled if it's entirely outside the min/max range
    return yearEnd < this.minDate || yearStart > this.maxDate;
  }

  // ===== Date Calculation =====
  
  private comparePeriods(a: PeriodSelection, b: PeriodSelection): number {
    if (a.year !== b.year) return a.year - b.year;
    return a.period - b.period;
  }

  private calculateAndEmitRange(): void {
    const dateRange = this.calculateDateRange();
    const value: PeriodRangeValue = {
      periodType: this.periodType,
      rangeStart: this.rangeStart,
      rangeEnd: this.rangeEnd,
      dateRange: dateRange
    };
    this.onChange(value);
  }

  private calculateDateRange(): PeriodRange | null {
    if (this.periodType === 'ytd') {
      const lastYear = new Date().getFullYear() - 1;
      const today = new Date();
      const startDate = new Date(lastYear, today.getMonth(), today.getDate());
      const endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      
      const clampedStart = startDate < this.minDate ? new Date(this.minDate) : startDate;
      const clampedEnd = endDate > this.maxDate ? new Date(this.maxDate) : endDate;
      
      return { startDate: clampedStart, endDate: clampedEnd };
    }

    if (!this.rangeStart || !this.rangeEnd) {
      return null;
    }

    let startDate: Date;
    let endDate: Date;

    switch (this.periodType) {
      case 'monthly':
        startDate = new Date(this.rangeStart.year, this.rangeStart.period - 1, 1);
        endDate = new Date(this.rangeEnd.year, this.rangeEnd.period, 0);
        break;
      
      case 'quarterly':
        const fromQuarterStartMonth = (this.rangeStart.period - 1) * 3;
        startDate = new Date(this.rangeStart.year, fromQuarterStartMonth, 1);
        const toQuarterStartMonth = (this.rangeEnd.period - 1) * 3;
        endDate = new Date(this.rangeEnd.year, toQuarterStartMonth + 3, 0);
        break;
      
      case 'yearly':
        startDate = new Date(this.rangeStart.year, 0, 1);
        endDate = new Date(this.rangeEnd.year, 11, 31);
        break;
      
      default:
        return null;
    }

    // Clamp dates to min/max boundaries
    const clampedStart = startDate < this.minDate ? new Date(this.minDate) : startDate;
    const clampedEnd = endDate > this.maxDate ? new Date(this.maxDate) : endDate;

    return { startDate: clampedStart, endDate: clampedEnd };
  }

  // ControlValueAccessor implementation
  writeValue(value: PeriodRangeValue | null): void {
    if (value) {
      // Set the values from the incoming value
      this.periodType = value.periodType;
      this.rangeStart = value.rangeStart;
      this.rangeEnd = value.rangeEnd;
    } else {
      // Reset to defaults
      this.periodType = 'ytd';
      this.rangeStart = null;
      this.rangeEnd = null;
    }
  }

  registerOnChange(fn: (value: PeriodRangeValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
